import { useEffect, useRef, useState } from 'react'
import { eur } from '../lib/format.ts'

interface Props {
  payoutCents: number
  baseAmountCents: number
  deductionsCents: number
  bonusCents: number
  /** Suelo garantizado por el tope de deduccion. null si no hay tope. */
  minGuaranteedCents: number | null
  capReached: boolean
}

const SIZE = 200
const STROKE = 14
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

/**
 * El saldo en vivo: el elemento central de la app.
 *
 * Con el modelo por excepcion el hijo casi no interactua con las tareas
 * obligatorias, asi que esta cifra es practicamente lo unico que consulta a
 * diario. Tiene que leerse de un vistazo y reaccionar visiblemente al cambiar.
 *
 * El anillo mide cuanto queda de la asignacion base; el bonus va aparte, porque
 * puede empujar el total por encima del 100 % y romperia la lectura del anillo.
 */
export function BalanceRing({
  payoutCents,
  baseAmountCents,
  deductionsCents,
  bonusCents,
  minGuaranteedCents,
  capReached,
}: Props) {
  const remaining = Math.max(0, baseAmountCents - deductionsCents)
  const ratio = baseAmountCents > 0 ? remaining / baseAmountCents : 1

  const color =
    ratio > 0.85 ? 'var(--color-good)' : ratio > 0.5 ? 'var(--color-warn)' : 'var(--color-bad)'

  // Anima al cambiar el importe, para que un descuento nuevo se note.
  const [pulse, setPulse] = useState(false)
  const previous = useRef(payoutCents)
  useEffect(() => {
    if (previous.current !== payoutCents) {
      previous.current = payoutCents
      setPulse(true)
      const timer = setTimeout(() => setPulse(false), 340)
      return () => clearTimeout(timer)
    }
  }, [payoutCents])

  return (
    <div className="flex flex-col items-center py-2">
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <svg width={SIZE} height={SIZE} className="-rotate-90">
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="var(--color-line)"
            strokeWidth={STROKE}
          />
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={color}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={CIRCUMFERENCE * (1 - ratio)}
            style={{ transition: 'stroke-dashoffset 600ms ease-out, stroke 400ms' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className={`text-[40px] font-bold leading-none tracking-tight ${pulse ? 'animate-pop' : ''}`}
          >
            {eur(payoutCents)}
          </span>
          <span className="mt-1 text-sm text-[var(--color-muted)]">de {eur(baseAmountCents)}</span>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
        {deductionsCents > 0 && (
          <span className="text-[var(--color-bad)]">−{eur(deductionsCents)}</span>
        )}
        {bonusCents > 0 && (
          <span className="text-[var(--color-good)]">+{eur(bonusCents)} bonus</span>
        )}
        {deductionsCents === 0 && bonusCents === 0 && (
          <span className="text-[var(--color-muted)]">Sin incidencias</span>
        )}
      </div>

      {minGuaranteedCents !== null && (
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Mínimo garantizado: {eur(minGuaranteedCents)}
        </p>
      )}

      {/* Que el tope se ha agotado no puede ser informacion oculta: cambia lo
          que esta en juego el resto de la semana, para el hijo y para el admin. */}
      {capReached && (
        <p className="mt-2 rounded-lg bg-[var(--color-warn)]/15 px-3 py-1.5 text-center text-xs text-[var(--color-warn)]">
          Has alcanzado el descuento máximo de esta semana
        </p>
      )}
    </div>
  )
}
